/**
 * Swap (Phase 8, spec §8): same-network swaps inside the custodial account.
 * Pick a network and two assets → type an amount (Half / Max) → a 30-second quote appears with
 * rate, price impact, fee, minimum received and a countdown → Swap now.
 * No 2FA code is needed (D53). On testnet the swap only changes ledger balances (D54).
 * Amounts stay decimal strings; Half/Max use BigInt base units.
 */
import type { SwapInfo, SwapMarket } from "@custos/api-client";
import { formatUnits } from "@custos/core";
import {
  Badge,
  Button,
  Card,
  Field,
  Notice,
  Segmented,
  Select,
  Skeleton,
  SuccessCheck,
  buttonClasses,
  cn,
  compareDecimalStrings,
  formatAmount,
  formatBps,
  formatFiat,
  isZeroAmount,
  normaliseAmountInput,
} from "@custos/ui";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownUp, RefreshCw } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router";
import { PageHeader } from "../components/AppShell";
import { AssetIcon, Row } from "../components/bits";
import { api } from "../lib/api";
import { errorMessage, isApiError } from "../lib/errors";
import { usePrefs } from "../lib/prefs";
import { qk, useBalances, usePortfolio, useSwapOptions } from "../lib/queries";

const SLIPPAGES = ["10", "50", "100", "300"] as const;

/** Waits until `value` has stopped changing for `ms`. */
function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/** Current time, ticking once a second while `on`. */
function useNow(on: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!on) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [on]);
  return now;
}

export function SwapPage() {
  const options = useSwapOptions();
  const balances = useBalances();
  const currency = usePrefs((s) => s.currency);
  const portfolio = usePortfolio(currency);
  const queryClient = useQueryClient();
  const [params] = useSearchParams();

  const markets = options.data?.markets ?? [];
  const [chain, setChain] = useState(params.get("chain") ?? "");
  const market: SwapMarket | undefined = markets.find((m) => m.chain === chain) ?? markets[0];
  const symbols = market?.assets.map((a) => a.symbol) ?? [];
  const [fromPick, setFrom] = useState(params.get("from") ?? "");
  const [toPick, setTo] = useState("");
  const from = symbols.includes(fromPick) ? fromPick : (symbols[0] ?? "");
  const to = symbols.includes(toPick) && toPick !== from ? toPick : (symbols.find((s) => s !== from) ?? "");
  const fromInfo = market?.assets.find((a) => a.symbol === from);
  const toInfo = market?.assets.find((a) => a.symbol === to);

  const [amountInput, setAmountInput] = useState("");
  const [slippage, setSlippage] = useState<(typeof SLIPPAGES)[number]>("50");
  const [result, setResult] = useState<SwapInfo | null>(null);

  const balanceOf = (symbol: string) =>
    balances.data?.find((b) => b.chain === market?.chain && b.asset === symbol);
  const fromBalance = balanceOf(from);
  const toBalance = balanceOf(to);

  const amount = fromInfo ? normaliseAmountInput(amountInput, fromInfo.decimals) : null;
  const problem = (() => {
    if (!amountInput.trim() || !fromInfo) return null;
    if (amount === null) return `Enter an amount with at most ${fromInfo.decimals} decimals.`;
    if (isZeroAmount(amount)) return null;
    if (fromBalance && compareDecimalStrings(amount, fromBalance.balance) > 0) return "Not enough balance.";
    if (compareDecimalStrings(amount, fromInfo.minAmount) < 0)
      return `The minimum is ${fromInfo.minAmount} ${from}.`;
    return null;
  })();
  const ready =
    !!market && !!fromInfo && !!toInfo && amount !== null && !isZeroAmount(amount) && problem === null;
  const request = useDebounced(
    ready
      ? { chain: market!.chain, fromAsset: from, toAsset: to, amount: amount!, slippageBps: Number(slippage) }
      : null,
    450,
  );
  const blocked = !options.data?.available || options.data.paused;

  const quote = useQuery({
    queryKey: ["swap-quote", request],
    queryFn: () => api.swapQuote(request!),
    enabled: request !== null && !blocked && !result,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
  });
  const q = quote.data && request && quote.data.fromAmount === request.amount ? quote.data : undefined;
  const now = useNow(!!q);
  // Counted from when the quote ARRIVED (not the server's clock), so a phone with a wrong clock
  // still shows the right countdown. The server checks the real expiry again on confirm.
  const secondsLeft = q ? Math.max(0, Math.ceil(q.ttlSeconds - (now - quote.dataUpdatedAt) / 1000)) : 0;
  const expired = !!q && secondsLeft === 0;
  // One idempotency key per quote: a double click or a network retry can never swap twice.
  const idempotencyKey = useMemo(() => (q ? crypto.randomUUID() : ""), [q]);

  const confirm = useMutation({
    mutationFn: () => api.confirmSwap(q!.id, idempotencyKey),
    onSuccess: async (swap) => {
      setResult(swap);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.balances }),
        queryClient.invalidateQueries({ queryKey: ["portfolio"] }),
        queryClient.invalidateQueries({ queryKey: qk.swaps }),
      ]);
    },
  });
  const staleQuote = isApiError(confirm.error, "QUOTE_EXPIRED") || isApiError(confirm.error, "PRICE_MOVED");

  const refresh = () => {
    confirm.reset();
    void quote.refetch();
  };
  const setPart = (part: "half" | "max") => {
    if (!fromBalance || !fromInfo) return;
    const units = BigInt(fromBalance.baseUnits);
    setAmountInput(formatUnits(part === "half" ? units / 2n : units, fromInfo.decimals));
    confirm.reset();
  };
  const flip = () => {
    setFrom(to);
    setTo(from);
    setAmountInput("");
    confirm.reset();
  };
  const fiat = (symbol: string, value: string | undefined) => {
    const price = portfolio.data?.assets.find((a) => a.chain === market?.chain && a.asset === symbol)?.price;
    if (!price || !value) return null;
    return formatFiat(fiatTimes(value, price), currency);
  };

  if (options.isPending) {
    return (
      <>
        <PageHeader title="Swap" />
        <Skeleton className="mx-auto h-96 max-w-lg rounded-[32px]" />
      </>
    );
  }
  if (options.error) {
    return (
      <>
        <PageHeader title="Swap" />
        <Notice tone="danger">{errorMessage(options.error)}</Notice>
      </>
    );
  }

  if (result) {
    return (
      <>
        <PageHeader title="Swap" />
        <Card
          className="mx-auto flex max-w-lg flex-col items-center gap-3 py-8 text-center"
          data-testid="swap-result"
        >
          <SuccessCheck className="size-20" />
          <h2 className="font-heading text-2xl font-semibold text-fg">Swap complete</h2>
          <p className="font-mono text-lg text-fg tabular-nums">
            −{formatAmount(result.fromAmount, 8)} {result.fromAsset} → +{formatAmount(result.toAmount, 8)}{" "}
            {result.toAsset}
          </p>
          {result.simulated && (
            <p className="max-w-sm text-sm text-muted">
              Testnet: only your account balances changed — no trade happened on the network.
            </p>
          )}
          <div className="mt-2 grid w-full max-w-sm grid-cols-1 gap-2 sm:grid-cols-2">
            <Link to="/activity" className={buttonClasses("secondary", "md", true)}>
              View activity
            </Link>
            <Button
              block
              onClick={() => {
                setResult(null);
                setAmountInput("");
                confirm.reset();
              }}
            >
              Swap again
            </Button>
          </div>
        </Card>
      </>
    );
  }

  return (
    <>
      <PageHeader title="Swap" subtitle="Same-network swaps with a clear quote before you confirm." />
      <div className="mx-auto flex max-w-lg flex-col gap-4">
        {!options.data.available && (
          <Notice tone="warning" title="Swaps aren't available here yet">
            Live (on-chain) swaps are switched on before mainnet use.
          </Notice>
        )}
        {options.data.paused && (
          <Notice tone="warning" title="Swaps are paused">
            An admin has paused swaps for now. Your balances are safe — try again later.
          </Notice>
        )}
        {options.data.simulated && options.data.available && (
          <div data-testid="swap-testnet-note">
            <Notice tone="info">
              Testnet: swaps use market prices but only change your account balances — no trade happens on the
              network.
            </Notice>
          </div>
        )}

        {markets.length > 0 && (
          <Field label="Network">
            {(p) => (
              <Select
                {...p}
                value={market?.chain ?? ""}
                onChange={(e) => {
                  setChain(e.target.value);
                  setFrom("");
                  setTo("");
                  setAmountInput("");
                  confirm.reset();
                }}
              >
                {markets.map((m) => (
                  <option key={m.chain} value={m.chain}>
                    {m.displayName === m.network ? m.displayName : `${m.displayName} · ${m.network}`}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        )}

        <div className="relative flex flex-col gap-2">
          {/* From */}
          <section aria-label="You send" className="glass rounded-[32px] p-5 pb-9 sm:p-6 sm:pb-10">
            <div className="flex items-center justify-between gap-3">
              <AssetPicker
                label="From asset"
                value={from}
                options={symbols}
                onChange={(v) => {
                  if (v === to) setTo(from);
                  setFrom(v);
                  confirm.reset();
                }}
              />
              <span className="rounded-full border border-line px-4 py-1.5 text-sm text-muted">Send</span>
            </div>
            <div className="mt-4 flex items-center justify-between gap-2">
              <button
                type="button"
                onClick={() => setPart("half")}
                disabled={!fromBalance || isZeroAmount(fromBalance.balance)}
                className="min-h-11 shrink-0 rounded-full border border-line bg-surface px-4 text-sm text-fg disabled:opacity-50"
              >
                Half
              </button>
              <input
                aria-label="Amount"
                inputMode="decimal"
                autoComplete="off"
                placeholder="0"
                value={amountInput}
                onChange={(e) => {
                  setAmountInput(e.target.value);
                  confirm.reset();
                }}
                className="w-0 min-w-0 flex-1 bg-transparent text-center font-normal text-4xl tracking-[-0.04em] text-fg tabular-nums placeholder:text-muted/60 focus:outline-none sm:text-5xl"
              />
              <button
                type="button"
                onClick={() => setPart("max")}
                disabled={!fromBalance || isZeroAmount(fromBalance.balance)}
                className="min-h-11 shrink-0 rounded-full border border-line bg-surface px-4 text-sm text-fg disabled:opacity-50"
              >
                Max
              </button>
            </div>
            <p className="mt-3 text-center text-sm text-muted">
              Balance: {fromBalance ? `${formatAmount(fromBalance.balance, 8)} ${from}` : "—"}
              {amount && fiat(from, amount) && <span className="text-muted"> · ≈ {fiat(from, amount)}</span>}
            </p>
          </section>

          <button
            type="button"
            onClick={flip}
            aria-label="Switch assets"
            className="absolute top-1/2 left-1/2 z-10 inline-flex size-16 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-primary text-on-primary shadow-pop transition-transform duration-200 active:scale-95"
          >
            <ArrowDownUp className="size-6" aria-hidden />
          </button>

          {/* To */}
          <section aria-label="You receive" className="glass rounded-[32px] p-5 pt-9 sm:p-6 sm:pt-10">
            <div className="flex items-center justify-between gap-3">
              <AssetPicker
                label="To asset"
                value={to}
                options={symbols.filter((s) => s !== from)}
                onChange={(v) => {
                  setTo(v);
                  confirm.reset();
                }}
              />
              <span className="rounded-full border border-line px-4 py-1.5 text-sm text-muted">Receive</span>
            </div>
            <p
              className={cn(
                "mt-4 truncate text-center text-4xl tracking-[-0.04em] tabular-nums sm:text-5xl",
                q ? "text-fg" : "text-muted",
              )}
              data-testid="swap-receive"
            >
              {q ? formatAmount(q.toAmount, 8) : quote.isFetching ? "…" : "0"}
            </p>
            <p className="mt-3 text-center text-sm text-muted">
              Balance: {toBalance ? `${formatAmount(toBalance.balance, 8)} ${to}` : "—"}
            </p>
          </section>
        </div>

        {problem && <Notice tone="warning">{problem}</Notice>}
        {quote.error && !problem && <Notice tone="danger">{errorMessage(quote.error)}</Notice>}
        {confirm.error && <Notice tone="danger">{errorMessage(confirm.error)}</Notice>}

        {expired || staleQuote ? (
          <Button block variant="secondary" onClick={refresh} loading={quote.isFetching}>
            <RefreshCw className="size-4" aria-hidden /> Refresh quote
          </Button>
        ) : (
          <Button
            block
            disabled={!q || blocked || quote.isFetching}
            loading={confirm.isPending}
            onClick={() => confirm.mutate()}
          >
            Swap now
          </Button>
        )}

        <Card className="flex flex-col gap-1" data-testid="swap-details">
          <Row label="Rate">{q ? `1 ${q.fromAsset} = ${formatAmount(q.rate, 8)} ${q.toAsset}` : "—"}</Row>
          <Row label="Price impact">{q ? formatBps(q.priceImpactBps) : "—"}</Row>
          <Row label="Fee">
            {q
              ? q.feeBps === 0
                ? "None"
                : `${formatAmount(q.feeAmount, 8)} ${q.toAsset} (${formatBps(q.feeBps)})`
              : "—"}
          </Row>
          <Row label="Minimum received">{q ? `${formatAmount(q.minToAmount, 8)} ${q.toAsset}` : "—"}</Row>
          <div className="flex flex-col gap-2 py-2">
            <span className="text-sm text-muted">Slippage tolerance</span>
            <Segmented
              label="Slippage tolerance"
              value={slippage}
              onChange={(v) => {
                setSlippage(v);
                confirm.reset();
              }}
              className="w-full flex-nowrap [&>button]:flex-1 [&>button]:px-2"
              options={SLIPPAGES.map((v) => ({ value: v, label: formatBps(Number(v)) }))}
            />
          </div>
          <p className="text-xs text-muted" data-testid="swap-countdown" aria-live="polite">
            {q
              ? expired
                ? "This quote has expired — refresh it to see the latest price."
                : `Quote expires in ${secondsLeft} s. You get at least the minimum shown, or the swap is refused.`
              : `Quotes are valid for ${options.data.quoteTtlSeconds} seconds.`}
            {options.data.maxPerSwapUsdCents !== null &&
              ` Maximum per swap: $${(options.data.maxPerSwapUsdCents / 100).toFixed(2)}.`}
          </p>
          {q?.simulated && (
            <Badge className="self-start" tone="primary">
              Testnet mock price
            </Badge>
          )}
        </Card>
      </div>
    </>
  );
}

function AssetPicker({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: string[];
  onChange: (value: string) => void;
}) {
  return (
    <label className="relative inline-flex min-w-0 items-center gap-2 rounded-full bg-surface/70 py-1.5 pr-4 pl-1.5 text-lg backdrop-blur">
      <AssetIcon symbol={value || "?"} className="size-10 text-base" />
      <span className="sr-only">{label}</span>
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="min-h-10 appearance-none bg-transparent pr-5 text-fg focus:outline-none [&>option]:bg-surface [&>option]:text-fg"
      >
        {options.map((s) => (
          <option key={s} value={s}>
            {s}
          </option>
        ))}
      </select>
      <span aria-hidden className="pointer-events-none absolute right-3 text-xs text-muted">
        ▾
      </span>
    </label>
  );
}

/** amount × price for an "≈ $…" hint, exactly (decimal strings → BigInt → cents). */
function fiatTimes(amount: string, price: string): string {
  const [aw = "0", af = ""] = amount.split(".");
  const [pw = "0", pf = ""] = price.split(".");
  const product = BigInt(aw + af) * BigInt(pw + pf); // scale: af.length + pf.length
  const scale = af.length + pf.length;
  const cents = scale >= 2 ? product / 10n ** BigInt(scale - 2) : product * 10n ** BigInt(2 - scale);
  return `${cents / 100n}.${(cents % 100n).toString().padStart(2, "0")}`;
}
